import { cachedCall } from '@/services/screenCache'
import { api } from './api'
import { ATYRAU_DISTRICTS } from '@/constants/atyrauDistricts'

/*
 * «Общественная безопасность» — общие ручки сайта вместо собранной под планшет
 * `/akim/public-safety/dashboard` (решение 29.09.2026: «как есть», один
 * источник с сайтом). Та ручка к тому же на проде падала по таймауту.
 *
 * КУИ — `/erdr/analytics/messages` (разрезы district, message, month): она
 * отвечает за доли секунды и даёт те же итоги, что сводка `/erdr/messages/
 * summary` сайта (у сводки 14 с). ЕРАП — `/erap/protocols/summary`, как плитка
 * сайта «Нарушения».
 *
 * ЧИСЛА ТЕПЕРЬ ПО ОПРЕДЕЛЕНИЯМ САЙТА, И ОНИ ДРУГИЕ, ЧЕМ БЫЛИ У ПЛАНШЕТА:
 * — КУИ считаются все органы и без исключения удалённых сообщений;
 * — ЕРАП — все протоколы, по дате правонарушения, район — место
 *   правонарушения (а было: действующие, по дате ввода, район — прокуратура
 *   органа выявления). Фильтра района у сводки ЕРАП нет: по району итог
 *   берётся из разбивки, а топ статей остаётся областным.
 * Помесячного разреза у ЕРАП нет, поэтому и динамики протоколов нет.
 */

export type PublicSafetyPeriod = 'week' | 'month' | 'quarter'

export type PublicSafetyKpi = {
  id: string
  label: string
  value: number
  previous: number
  deltaPercent: number | null
  accent: 'up' | 'down' | 'flat'
  deltaLabel: string
  hint?: string | null
}

export type PublicSafetyNamedValue = {
  id: string
  name: string
  value: number
  previous?: number
  share?: number
  deltaPercent?: number | null
  shortName?: string
}

export type PublicSafetyDistrictRow = {
  id: string
  name: string
  total: number
  previous: number
  deltaPercent: number | null
}

export type PublicSafetyPoint = {
  key: string
  label: string
  value: number
}

export type PublicSafetyDashboard = {
  period: { unit: string }
  district: { name: string } | null
  kpis: PublicSafetyKpi[]
  kui: {
    total: number
    previous: number
    deltaPercent: number | null
    byType: PublicSafetyNamedValue[]
    dynamics: PublicSafetyPoint[]
    districts: PublicSafetyDistrictRow[]
  }
  admin: {
    total: number
    previous: number
    deltaPercent: number | null
    topArticles: PublicSafetyNamedValue[]
    /** Всегда пуст: помесячного разреза у ЕРАП нет. */
    dynamics: PublicSafetyPoint[]
    districts: PublicSafetyDistrictRow[]
    periodLabel: string
  }
  sourceNote: string
}

/* ─── Ручки ─── */

type Analytics = {
  total: number
  items: Array<{ key: string; name: string; count: number }>
}

type ErapRef = { id: number; code?: string | null; nameRu?: string | null }
type ErapSummary = {
  total: number
  byDistrict: Array<{ ref: ErapRef | null; count: number }>
  topArticles: Array<{ ref: ErapRef | null; count: number }>
}

const kuiAnalytics = (groupBy: string, range: Range, extra: Record<string, string | number | undefined> = {}) =>
  api.get<Analytics>('/erdr/analytics/messages', {
    params: { groupBy, groups: 30, dateFrom: range.from, dateTo: range.to, ...extra },
  })

const erapSummary = (range: Range) =>
  api.get<ErapSummary>('/erap/protocols/summary', { params: { from: range.from, to: range.to } })

/* ─── Окна ─── */

type Range = { from: string; to: string }

const PERIOD_DAYS: Record<PublicSafetyPeriod, number> = { week: 7, month: 30, quarter: 90 }
const DAY = 86_400_000
const MONTHS_RU = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']

function regionToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Atyrau',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

const shift = (iso: string, days: number) =>
  new Date(Date.parse(`${iso}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10)

/** Скользящее окно по сегодня и предыдущее окно той же длины. */
function windows(unit: PublicSafetyPeriod): { curr: Range; prev: Range } {
  const to = regionToday()
  const n = PERIOD_DAYS[unit]
  const from = shift(to, -(n - 1))
  return { curr: { from, to }, prev: { from: shift(from, -n), to: shift(from, -1) } }
}

function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/* ─── Районы ─── */

/** КУИ пишут город «Атырау», ЕРАП — «г.Атырау» и «Кзылкогинский»; приводим к справочнику. */
function ourDistrict(raw: string | null | undefined): string | null {
  const t = (raw ?? '').replace(/\s+/g, ' ').trim()
  if (!t) return null
  if (/^(г\.\s?)?атырау$/i.test(t)) return 'г. Атырау'
  if (/к[ы]?зылкогин/i.test(t)) return 'Кызылкогинский район'
  return ATYRAU_DISTRICTS.find((d) => d.toLowerCase() === t.toLowerCase()) ?? null
}

/* ─── Помощники — те же формулы, что были у бэкенда ─── */

function deltaPercent(curr: number, prev: number): number | null {
  if (prev === 0) return curr > 0 ? 100 : null
  return Math.round(((curr - prev) / prev) * 1000) / 10
}

function makeKpi(id: string, label: string, c: number, p: number, hint?: string): PublicSafetyKpi {
  const d = deltaPercent(c, p)
  return {
    id,
    label,
    value: c,
    previous: p,
    deltaPercent: d,
    accent: c === p ? 'flat' : c > p ? 'up' : 'down',
    deltaLabel:
      d == null
        ? 'нет базы сравнения'
        : d === 0
          ? 'без изменений'
          : `${d > 0 ? '▲' : '▼'} ${Math.abs(d)}% к прошлому периоду (${p.toLocaleString('ru-RU')})`,
    hint: hint ?? null,
  }
}

function shortLabel(text: string, max: number): string {
  const t = text.trim()
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`
}

function districtRows(
  curr: Array<{ name: string | null; count: number }>,
  prev: Array<{ name: string | null; count: number }>,
  only: string | null,
): PublicSafetyDistrictRow[] {
  const sum = (rows: typeof curr) => {
    const m = new Map<string, number>()
    for (const r of rows) {
      const d = ourDistrict(r.name)
      if (d) m.set(d, (m.get(d) ?? 0) + r.count)
    }
    return m
  }
  const c = sum(curr)
  const p = sum(prev)
  return ATYRAU_DISTRICTS.filter((d) => !only || d === only)
    .map((d) => {
      const total = c.get(d) ?? 0
      const previous = p.get(d) ?? 0
      return { id: d, name: d, total, previous, deltaPercent: deltaPercent(total, previous) }
    })
    .sort((a, b) => b.total - a.total)
}

/* ─── Сборка экрана ─── */

async function fetchPublicSafetyDashboardRaw(params: {
  unit: PublicSafetyPeriod
  /** Район из справочника планшета; пусто — вся область. */
  district?: string | null
}): Promise<PublicSafetyDashboard> {
  const district = params.district || null
  const { curr, prev } = windows(params.unit)

  // КУИ по районам — один запрос на окно; id района КУИ нужен для остальных разрезов.
  const [kuiDistCurr, kuiDistPrev] = await Promise.all([kuiAnalytics('district', curr), kuiAnalytics('district', prev)])
  const kuiDistrictId = district
    ? kuiDistCurr.items.find((i) => ourDistrict(i.name) === district)?.key
    : undefined
  const scoped = kuiDistrictId ? { districtId: kuiDistrictId } : {}

  const today = regionToday()
  const firstMonth = (() => {
    const [y, m] = today.split('-').map(Number)
    return new Date(Date.UTC(y!, m! - 1 - 11, 1)).toISOString().slice(0, 10)
  })()

  const [typesCurr, typesPrev, months, erapCurr, erapPrev] = await Promise.all([
    kuiAnalytics('message', curr, scoped),
    kuiAnalytics('message', prev, scoped),
    kuiAnalytics('month', { from: firstMonth, to: today }, { ...scoped, groups: 13 }),
    erapSummary(curr),
    erapSummary(prev),
  ])

  /* ─── КУИ ─── */

  const kuiDistricts = districtRows(
    kuiDistCurr.items.map((i) => ({ name: i.name, count: i.count })),
    kuiDistPrev.items.map((i) => ({ name: i.name, count: i.count })),
    district,
  )
  const kuiCurr = district ? (kuiDistricts[0]?.total ?? 0) : kuiDistCurr.total
  const kuiPrev = district ? (kuiDistricts[0]?.previous ?? 0) : kuiDistPrev.total

  const prevByType = new Map(typesPrev.items.map((i) => [i.key, i.count]))
  const typesTotal = typesCurr.items.reduce((s, i) => s + i.count, 0) || 1
  const kuiTypes: PublicSafetyNamedValue[] = [...typesCurr.items]
    .sort((a, b) => b.count - a.count)
    .map((i) => {
      const previous = prevByType.get(i.key) ?? 0
      return {
        id: i.key,
        name: i.name,
        value: i.count,
        previous,
        share: Math.round((i.count / typesTotal) * 1000) / 10,
        deltaPercent: deltaPercent(i.count, previous),
      }
    })

  const monthCounts = new Map(months.items.map((i) => [i.key, i.count]))
  const kuiDynamics: PublicSafetyPoint[] = Array.from({ length: 12 }, (_, k) => {
    const d = new Date(Date.parse(`${firstMonth}T00:00:00Z`))
    d.setUTCMonth(d.getUTCMonth() + k)
    const key = d.toISOString().slice(0, 7)
    return { key, label: MONTHS_RU[d.getUTCMonth()]!, value: monthCounts.get(key) ?? 0 }
  })

  /* ─── ЕРАП ─── */

  const adminDistricts = districtRows(
    erapCurr.byDistrict.map((r) => ({ name: r.ref?.nameRu ?? null, count: r.count })),
    erapPrev.byDistrict.map((r) => ({ name: r.ref?.nameRu ?? null, count: r.count })),
    district,
  )
  const adminCurr = district ? (adminDistricts[0]?.total ?? 0) : erapCurr.total
  const adminPrev = district ? (adminDistricts[0]?.previous ?? 0) : erapPrev.total

  const prevByArticle = new Map(erapPrev.topArticles.map((a) => [a.ref?.code ?? '', a.count]))
  const topArticles: PublicSafetyNamedValue[] = erapCurr.topArticles.slice(0, 12).map((a) => {
    const code = a.ref?.code ?? ''
    const name = a.ref?.nameRu?.trim() || code || '—'
    const previous = prevByArticle.get(code) ?? 0
    return { id: code, name, shortName: name, value: a.count, previous, deltaPercent: deltaPercent(a.count, previous) }
  })

  const windowLabel = `${formatDay(curr.from)} — ${formatDay(curr.to)}`
  const topType = kuiTypes[0] ?? null
  const topArticle = topArticles[0] ?? null

  return {
    period: { unit: params.unit },
    district: district ? { name: district } : null,
    kpis: [
      makeKpi('kui_total', 'Заявлений КУИ', kuiCurr, kuiPrev, 'Регистрация сообщений, без уголовных дел'),
      makeKpi('admin_total', 'Адм. протоколов', adminCurr, adminPrev, `ЕРАП · ${windowLabel}`),
      makeKpi(
        'kui_top_type',
        topType ? `КУИ · ${shortLabel(topType.name, 28)}` : 'КУИ · топ-тип',
        topType?.value ?? 0,
        topType?.previous ?? 0,
        topType?.name,
      ),
      makeKpi(
        'admin_top_article',
        topArticle ? `ЕРАП · ${topArticle.shortName}${district ? ' (область)' : ''}` : 'ЕРАП · топ-статья',
        topArticle?.value ?? 0,
        topArticle?.previous ?? 0,
        topArticle?.name,
      ),
    ],
    kui: {
      total: kuiCurr,
      previous: kuiPrev,
      deltaPercent: deltaPercent(kuiCurr, kuiPrev),
      byType: kuiTypes,
      dynamics: kuiDynamics,
      districts: kuiDistricts,
    },
    admin: {
      total: adminCurr,
      previous: adminPrev,
      deltaPercent: deltaPercent(adminCurr, adminPrev),
      topArticles,
      dynamics: [],
      districts: adminDistricts,
      periodLabel: district ? `${windowLabel} · топ статей по области` : windowLabel,
    },
    sourceNote:
      'Те же ручки, что у сайта: КУИ — /erdr/analytics/messages, ЕРАП — /erap/protocols/summary. ' +
      'КУИ — все органы, включая удалённые сообщения; ЕРАП — все протоколы по дате правонарушения, ' +
      'район — место правонарушения. Помесячного разреза у ЕРАП нет.',
  }
}

/**
 * Экран целиком — в `screenCache` на час: собранная сводка весит килобайты и
 * переживает перезагрузку вкладки, повторное открытие — без запросов. Сырые
 * ответы при этом живут только в памяти (`api.ts`). Кнопка «Обновить» сбрасывает
 * ключ через `invalidateScreenCache`.
 */
export function fetchPublicSafetyDashboard(params: {
  unit: PublicSafetyPeriod
  /** Район из справочника планшета; пусто — вся область. */
  district?: string | null
}): Promise<PublicSafetyDashboard> {
  return cachedCall(`public-safety/dashboard:${params.unit}:${params.district || 'all'}`, () => fetchPublicSafetyDashboardRaw(params))
}
